/**
 * Панель «PR-кандидаты» админ-ревью обновлений.
 *
 * Показывает смердженные PR, из которых ещё не сделаны карточки «Что нового».
 * Админ фильтрует по агенту, отмечает нужные галочками, выбирает режим
 * (карточка на каждый PR / объединить в одну) и запускает фоновую генерацию.
 * Джоба пишет ЧЕРНОВИКИ — публикация остаётся отдельным ручным гейтом ниже.
 *
 * FSD: feature — импортирует из entities/shared/app, наружу отдаёт только UI.
 */

import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '@app/hooks';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Field, Label, Select } from '@shared/ui/Input';
import { IconChevronDown, IconFileStack } from '@shared/ui/icons';
import { AGENT_LABELS, ANNOUNCEABLE_AGENT_CODES } from '@shared/agents/labels';
import {
  osnovaApi,
  useGetPrCandidatesQuery,
  useGenerateAnnouncementsMutation,
  useGetGenBatchStatusQuery,
} from '@shared/api';
import {
  announcementAgentLabel,
  kindMeta,
  type AnnouncementKind,
  type AnnouncementMergeMode,
} from '@entities/announcement';

/* ── options ─────────────────────────────────────────────────────────────── */

/**
 * Фильтр списка кандидатов. «Все агенты» (`all`) и «Система» (`system`) —
 * РАЗНЫЕ опции: `all` показывает всё, `system` — только общесистемные PR.
 */
const AGENT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Все агенты' },
  { value: 'system', label: 'Система' },
  ...ANNOUNCEABLE_AGENT_CODES.map((value) => ({ value, label: AGENT_LABELS[value] ?? value })),
];

/** Целевой агент карточки при объединении PR в одну (merge_mode=combined). */
const SCOPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'auto', label: 'Определить автоматически' },
  { value: 'system', label: 'Система' },
  ...ANNOUNCEABLE_AGENT_CODES.map((value) => ({ value, label: AGENT_LABELS[value] ?? value })),
];

const POLL_MS = 4000;

/** По умолчанию показываем только N последних PR (список приходит с backend
 *  отсортированным свежими первыми). Остальное — под кнопкой «Раскрыть ещё». */
const DEFAULT_VISIBLE = 5;

/** Дата + часы:минуты мерджа (ru-RU). `null`/невалидное — прочерк. */
function formatDtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function apiError(err: unknown, fallback: string): string {
  // RTK Query отдаёт ошибку как `unknown`; сужаем до формы конверта бэкенда
  // `{ data: { error: { message } } }`. Каждый доступ опционален (`?.`), поэтому
  // при любом другом shape вернём fallback — приведение безопасно.
  return (err as { data?: { error?: { message?: string } } })?.data?.error?.message ?? fallback;
}

/* ── layout ──────────────────────────────────────────────────────────────── */

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset ${({ theme }) => theme.shadow.focus};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HeaderCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Chevron = styled(IconChevronDown)<{ $open: boolean }>`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.text.muted};
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Body = styled.div`
  padding: 0 ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Lead = styled.p`
  margin: ${({ theme }) => theme.spacing(4)} 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  margin: ${({ theme }) => theme.spacing(4)} 0;
`;

const FilterField = styled(Field)`
  margin: 0;
  min-width: 200px;
  flex: 1 1 200px;
`;

const SelectAllButton = styled.button`
  align-self: center;
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing(1)} 0;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.xs};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.text.muted};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled.li`
  display: block;
`;

const RowLabel = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: start;
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:has(:focus-visible) {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  -webkit-appearance: none;
  margin: 2px 0 0;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: grid;
  place-content: center;
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    transform: scale(0);
    transform-origin: center;
    background: #fff;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.spring};
  }
  &:checked {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:checked::before {
    transform: scale(1);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::before {
      transition: none;
    }
  }
`;

const RowMain = styled.div`
  min-width: 0;
`;

const RowTitle = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const PrNum = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: ${({ theme }) => theme.spacing(2)};
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const MergedAt = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Author = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Текстовая кнопка «Раскрыть ещё» / «Свернуть» под списком кандидатов. */
const ExpandButton = styled.button`
  display: block;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const KBadge = styled(Badge)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Segment = styled.div`
  display: inline-flex;
  gap: 3px;
  padding: 3px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  background: ${({ theme, $active }) => ($active ? theme.colors.backgroundElevated : 'transparent')};
  box-shadow: ${({ theme, $active }) => ($active ? theme.shadow.xs : 'none')};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Spacer = styled.div`
  flex: 1 1 auto;
`;

const ResultBox = styled.div<{ $tone: 'info' | 'success' | 'danger' }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme, $tone }) => theme.colors.status[$tone === 'danger' ? 'danger' : $tone]};
  background: ${({ theme, $tone }) =>
    `${theme.colors.status[$tone === 'danger' ? 'danger' : $tone]}15`};
  border: 1px solid
    ${({ theme, $tone }) => `${theme.colors.status[$tone === 'danger' ? 'danger' : $tone]}33`};
`;

const StateNote = styled.p`
  margin: ${({ theme }) => theme.spacing(4)} 0 0;
  padding: ${({ theme }) => theme.spacing(6)};
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/* ── kind badge ──────────────────────────────────────────────────────────── */

function KindBadge({ kind }: { kind: AnnouncementKind }) {
  const meta = kindMeta(kind);
  const { Icon } = meta;
  return (
    <KBadge $tone={meta.tone}>
      <Icon size={12} />
      {meta.label}
    </KBadge>
  );
}

/* ── panel ───────────────────────────────────────────────────────────────── */

export function PrCandidatesPanel() {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(true);
  const [agent, setAgent] = useState('all');
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [mergeMode, setMergeMode] = useState<AnnouncementMergeMode>('per_pr');
  const [combinedScope, setCombinedScope] = useState('auto');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [poll, setPoll] = useState(0);
  const [genError, setGenError] = useState<string | null>(null);

  const {
    data: candidates = [],
    isFetching,
    isError,
  } = useGetPrCandidatesQuery(agent);
  const [generate, { isLoading: isGenerating }] = useGenerateAnnouncementsMutation();
  const { data: batch } = useGetGenBatchStatusQuery(batchId ?? '', {
    skip: !batchId,
    pollingInterval: poll,
  });

  // Поллим статус джобы, пока она не терминальна.
  useEffect(() => {
    if (!batchId) {
      setPoll(0);
      return;
    }
    const terminal = batch?.status === 'done' || batch?.status === 'error';
    setPoll(terminal ? 0 : POLL_MS);
  }, [batchId, batch?.status]);

  // Джоба пишет черновики асинхронно — на терминальном `done` инвалидируем
  // теги, чтобы нижняя секция черновиков и список кандидатов подтянули новое.
  useEffect(() => {
    if (batch?.status === 'done') {
      setSelected(new Set());
      dispatch(osnovaApi.util.invalidateTags(['AdminAnnouncement', 'PrCandidate']));
    }
  }, [batch?.status, dispatch]);

  const prNumbers = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);
  // По умолчанию — 5 последних (backend уже отдал свежими первыми, не пересортировываем).
  const visible = expanded ? candidates : candidates.slice(0, DEFAULT_VISIBLE);
  const allSelected = candidates.length > 0 && candidates.every((c) => selected.has(c.number));
  const jobActive = batch?.status === 'queued' || batch?.status === 'running';
  const busy = isGenerating || jobActive;

  function toggle(n: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(candidates.map((c) => c.number)));
  }

  function changeAgent(value: string) {
    setAgent(value);
    setSelected(new Set()); // выбор не переносим между фильтрами — что видно, то и уйдёт
    setExpanded(false); // при смене фильтра список сворачиваем обратно к 5 последним
  }

  async function handleGenerate() {
    if (prNumbers.length === 0 || busy) return;
    setGenError(null);
    const scope = mergeMode === 'combined' ? combinedScope : 'auto';
    try {
      const { batch_id } = await generate({
        pr_numbers: prNumbers,
        scope,
        merge_mode: mergeMode,
      }).unwrap();
      setBatchId(batch_id);
    } catch (err) {
      setGenError(apiError(err, 'Не удалось запустить генерацию'));
    }
  }

  const bodyId = 'pr-candidates-body';

  return (
    <Section>
      <HeaderButton
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <IconFileStack size={16} aria-hidden />
        <HeaderTitle>PR-кандидаты</HeaderTitle>
        <HeaderCount>({candidates.length})</HeaderCount>
        <Chevron $open={open} size={18} aria-hidden />
      </HeaderButton>

      {open && (
        <Body id={bodyId}>
          <Lead>
            Смердженные PR, из которых ещё не сделаны карточки. Отметьте нужные и сгенерируйте
            черновики — они появятся ниже на ревью перед публикацией.
          </Lead>

          <Controls>
            <FilterField>
              <Label htmlFor="pr-agent-filter">Агент</Label>
              <Select
                id="pr-agent-filter"
                value={agent}
                onChange={(e) => changeAgent(e.target.value)}
              >
                {AGENT_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FilterField>

            <SelectAllButton
              type="button"
              onClick={toggleAll}
              disabled={candidates.length === 0 || busy}
            >
              {allSelected ? 'Снять выбор' : 'Выбрать все'}
            </SelectAllButton>
          </Controls>

          {isFetching && candidates.length === 0 ? (
            <StateNote>Загрузка…</StateNote>
          ) : isError ? (
            <StateNote>Не удалось загрузить список PR. Попробуйте позже.</StateNote>
          ) : candidates.length === 0 ? (
            <StateNote>Нет PR-кандидатов для этого фильтра.</StateNote>
          ) : (
            <>
              <List>
                {visible.map((c) => (
                  <Row key={c.number}>
                    <RowLabel>
                      <Checkbox
                        checked={selected.has(c.number)}
                        onChange={() => toggle(c.number)}
                        disabled={busy}
                      />
                      <RowMain>
                        <RowTitle>
                          <PrNum>#{c.number}</PrNum>
                          {c.title}
                        </RowTitle>
                        <RowMeta>
                          <Badge $tone="neutral">
                            {announcementAgentLabel(c.suggested_agent_code)}
                          </Badge>
                          <KindBadge kind={c.suggested_kind} />
                          {c.author && <Author>автор: {c.author}</Author>}
                          <MergedAt>Смерджен: {formatDtTime(c.merged_at)}</MergedAt>
                        </RowMeta>
                      </RowMain>
                    </RowLabel>
                  </Row>
                ))}
              </List>

              {candidates.length > DEFAULT_VISIBLE && (
                <ExpandButton
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded
                    ? 'Свернуть'
                    : `Раскрыть ещё (${candidates.length - DEFAULT_VISIBLE})`}
                </ExpandButton>
              )}
            </>
          )}

          <Footer>
            <Segment role="group" aria-label="Режим генерации">
              <SegmentButton
                type="button"
                $active={mergeMode === 'per_pr'}
                aria-pressed={mergeMode === 'per_pr'}
                onClick={() => setMergeMode('per_pr')}
              >
                Карточка на каждый PR
              </SegmentButton>
              <SegmentButton
                type="button"
                $active={mergeMode === 'combined'}
                aria-pressed={mergeMode === 'combined'}
                onClick={() => setMergeMode('combined')}
              >
                Объединить в одну
              </SegmentButton>
            </Segment>

            {mergeMode === 'combined' && (
              <FilterField>
                <Label htmlFor="pr-combined-scope">Агент карточки</Label>
                <Select
                  id="pr-combined-scope"
                  value={combinedScope}
                  onChange={(e) => setCombinedScope(e.target.value)}
                >
                  {SCOPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}

            <Spacer />

            <Button
              type="button"
              $variant="primary"
              disabled={prNumbers.length === 0 || busy}
              onClick={handleGenerate}
            >
              {busy
                ? 'Генерация…'
                : `Сгенерировать черновики${prNumbers.length ? ` (${prNumbers.length})` : ''}`}
            </Button>
          </Footer>

          {genError ? (
            <ResultBox $tone="danger">{genError}</ResultBox>
          ) : jobActive ? (
            <ResultBox $tone="info">Генерация черновиков…</ResultBox>
          ) : batch?.status === 'done' ? (
            <ResultBox $tone="success">
              Создано: {batch.created_count}. Пропущено (уже были): {batch.skipped_count}.
            </ResultBox>
          ) : batch?.status === 'error' ? (
            <ResultBox $tone="danger">
              {batch.error ?? 'Не удалось сгенерировать черновики.'}
            </ResultBox>
          ) : null}
        </Body>
      )}
    </Section>
  );
}
